// ==============================
// WebGL capability detection
// ==============================
function is3DSupported() {
  try {
    const canvas = document.createElement('canvas');

    // Force WebGL1
    const gl =
      canvas.getContext('webgl', { failIfMajorPerformanceCaveat: true }) ||
      canvas.getContext('experimental-webgl');

    if (!gl) return false;

    // Required for MeshStandardMaterial
    const derivatives = gl.getExtension('OES_standard_derivatives');
    if (!derivatives) return false;

    return true;
  } catch (e) {
    return false;
  }
}

// ==============================
// Fallback to static image
// ==============================
function showStaticImage(container) {
  container.classList.add('is-3d-failed');

  // Remove canvas if any
  const canvas = container.querySelector('canvas');
  if (canvas) canvas.remove();

  // Reveal image inside product card
  const img =
    container.closest('.product-card-wrapper')?.querySelector('img');

  if (img) {
    img.style.opacity = '1';
    img.style.visibility = 'visible';
  }
}



// ==============================
// Helper to dispose old 3D objects
// ==============================
function disposeContainer(container) {
  if (container._can) {
    container._can.traverse(child => {
      if (child.isMesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      }
    });
    if (container._scene) container._scene.remove(container._can);
  }

  if (container._renderer) {
    container._renderer.dispose();
  }

  const oldCanvas = container.querySelector('canvas');
  if (oldCanvas) oldCanvas.remove();

  container._scene = null;
  container._renderer = null;
  container._can = null;
}

// ==============================
// Mark 3D as failed
// ==============================
function mark3DFailed(container, reason = '') {
  console.warn('❌ 3D disabled:', reason);
  container.classList.add('is-3d-failed');
  disposeContainer(container);
  showStaticImage(container);
}


// ==============================
// Initialize 3D for a single container
// ==============================
function initSingle3D(container) {
  // Do not retry
  if (container.classList.contains('is-3d-failed')) return;

  // Capability check
  if (!is3DSupported()) {
    mark3DFailed(container, 'device-not-supported');
    return;
  }
  disposeContainer(container);

  const DELAY = 300;

  setTimeout(() => {
    try {
      const modelURL =
        'https://cdn.shopify.com/3d/models/fc202c6b7e4396dc/can-model.glb';
      const labelURL = container.dataset.texture;

      if (!labelURL) throw new Error('Missing texture');
      if (!container.clientWidth || !container.clientHeight) {
        throw new Error('Container has no size');
      }

      const loader = new THREE.GLTFLoader();
      const textureLoader = new THREE.TextureLoader();

      loader.load(
        modelURL,
        gltf => {
          try {
            // Scene
            const scene = new THREE.Scene();
            scene.background = new THREE.Color(
              container.dataset.color || '#f8f8f8'
            );
            container._scene = scene;

            // Camera
            const camera = new THREE.PerspectiveCamera(
              50,
              container.clientWidth / container.clientHeight,
              0.1,
              100
            );
            camera.position.set(0, 0, 10);

            // Renderer
            const renderer = new THREE.WebGLRenderer({
              antialias: true,
              alpha: true,
            });
            renderer.outputEncoding = THREE.sRGBEncoding;
            renderer.setSize(
              container.clientWidth,
              container.clientHeight
            );

            container.appendChild(renderer.domElement);
            container._renderer = renderer;

            // WebGL context loss detection
            renderer.domElement.addEventListener(
              'webglcontextlost',
              e => {
                e.preventDefault();
                mark3DFailed(container, 'webgl-context-lost');
              },
              false
            );

            // Controls
            const controls = new THREE.OrbitControls(
              camera,
              renderer.domElement
            );
            controls.enableZoom = false;
            controls.enablePan = false;
            controls.enableDamping = true;
            controls.dampingFactor = 0.05;
            controls.minPolarAngle = Math.PI / 2;
            controls.maxPolarAngle = Math.PI / 2;

            controls.userIsInteracting = false;
            controls.addEventListener(
              'start',
              () => (controls.userIsInteracting = true)
            );
            controls.addEventListener(
              'end',
              () => (controls.userIsInteracting = false)
            );

            // Lights
            const hemiLight = new THREE.HemisphereLight(
              0xffffff,
              0x555555,
              1
            );
            hemiLight.position.set(0, 20, 0);
            scene.add(hemiLight);

            const dirLight = new THREE.DirectionalLight(0xffffff, 1);
            dirLight.position.set(5, 5, 5);
            scene.add(dirLight);

            // Texture
            const labelTexture = textureLoader.load(
              labelURL,
              t => {
                t.minFilter = THREE.LinearFilter;
                t.magFilter = THREE.LinearFilter;
                t.anisotropy =
                  renderer.capabilities.getMaxAnisotropy();
              },
              undefined,
              err => {
                console.error('❌ Texture load error', err);
                mark3DFailed(container, 'texture-load-error');
              }
            );

            labelTexture.encoding = THREE.sRGBEncoding;
            labelTexture.wrapS = THREE.ClampToEdgeWrapping;
            labelTexture.wrapT = THREE.ClampToEdgeWrapping;

            // Model
            const can = gltf.scene.clone();
            can.scale.set(7, 7, 7);

            const box = new THREE.Box3().setFromObject(can);
            const center = box.getCenter(new THREE.Vector3());
            can.position.sub(center);

            can.rotation.y = 5.3;
            container._can = can;

            can.traverse(child => {
              if (child.isMesh) {
                if (child.name.includes('Circle_Label_0')) {
                  child.material = new THREE.MeshStandardMaterial({
                    map: labelTexture,
                    metalness: 0.3,
                    roughness: 0.35,
                  });
                } else {
                  child.material = new THREE.MeshStandardMaterial({
                    color: 0xcccccc,
                    metalness: 0.9,
                    roughness: 0.4,
                  });
                }
              }
            });

            scene.add(can);

            // Hover rotation
            let isHovering = false;
            let targetRotationY = 5.3;

            const card = container.closest('.product-card-wrapper');
            if (card) {
              card.addEventListener(
                'mouseenter',
                () => (isHovering = true)
              );
              card.addEventListener('mouseleave', () => {
                isHovering = false;
                targetRotationY = 5.3;
              });
            }

            // Animation loop
            function animate() {
              try {
                requestAnimationFrame(animate);

                if (can && !controls.userIsInteracting) {
                  if (isHovering) {
                    can.rotation.y -= 0.02;
                    targetRotationY = can.rotation.y;
                  } else {
                    can.rotation.y +=
                      (targetRotationY - can.rotation.y) * 0.1;
                  }
                }

                controls.update();
                renderer.render(scene, camera);
              } catch (err) {
                mark3DFailed(container, 'runtime-error');
              }
            }
            animate();

            // Resize
            window.addEventListener('resize', () => {
              if (!container._renderer) return;
              camera.aspect =
                container.clientWidth / container.clientHeight;
              camera.updateProjectionMatrix();
              renderer.setSize(
                container.clientWidth,
                container.clientHeight
              );
            });

            // Hide loaders
            setTimeout(() => {
              document
                .querySelectorAll('.product-card-image-loader')
                .forEach(el => el.classList.add('hide'));
            }, 300);
          } catch (err) {
            mark3DFailed(container, 'scene-init-error');
          }
        },
        undefined,
        err => {
          console.error('❌ Model load error', err);
          mark3DFailed(container, 'model-load-error');
        }
      );
    } catch (err) {
      mark3DFailed(container, 'init-error');
    }
  }, DELAY);
}

// ==============================
// Init on page load
// ==============================
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product-image-3d').forEach(container => {
    initSingle3D(container);
  });
});

// ==============================
// Re-init after Shopify AJAX filtering
// ==============================
if (typeof FacetFiltersForm !== 'undefined') {
  const originalRender =
    FacetFiltersForm.renderProductGridContainer;

  FacetFiltersForm.renderProductGridContainer = function (html) {
    originalRender.call(this, html);
    document.querySelectorAll('.product-image-3d').forEach(container => {
      initSingle3D(container);
    });
  };
}
